import React, { useState, useEffect } from 'react';
import { Table, Card, DatePicker, Button, Space, Tag, Statistic, Row, Col, message, Select } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faDollarSign } from '@fortawesome/free-solid-svg-icons';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import moment from 'moment';
import axios from 'axios';

const { RangePicker } = DatePicker;
const { Option } = Select;

// Preset date ranges
const datePresets = [
  { label: 'Last 7 Days', value: 7 },
  { label: 'Last 14 Days', value: 14 },
  { label: 'Last 30 Days', value: 30 },
  { label: 'Last 3 Months', value: 90 },
  { label: 'Last 6 Months', value: 180 }
];

const GoogleAdsCampaignsTable = () => {
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(90); // Default to 3 months
  const [dateRange, setDateRange] = useState([
    moment().subtract(90, 'days'),
    moment()
  ]);

  useEffect(() => {
    loadAvailableCampaigns();
  }, []);

  useEffect(() => {
    if (dateRange && dateRange.length === 2) {
      loadCampaigns();
    }
  }, [dateRange, selectedCampaign]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      
      // Validate dateRange before destructuring
      if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2) {
        console.error('Invalid date range:', dateRange);
        message.error('Invalid date range');
        setLoading(false);
        return;
      }
      
      const [startDate, endDate] = dateRange;
      
      // Ensure both dates are moment objects
      if (!startDate || !endDate || !moment.isMoment(startDate) || !moment.isMoment(endDate)) {
        console.error('Invalid date objects:', { startDate, endDate });
        message.error('Invalid date selection');
        setLoading(false);
        return;
      }
      
      const response = await axios.get('http://localhost:5000/api/call-analytics/google-ads-calls', {
        params: {
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD'),
          ...(selectedCampaign !== 'all' && { campaignName: selectedCampaign })
        }
      });
      
      setCampaigns(response.data.data || response.data || []);
    } catch (error) {
      console.error('Error loading Google Ads data:', error);
      message.error('Failed to load campaign data');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableCampaigns = async () => {
    try {
      const response = await axios.get('http://localhost:5000/api/call-analytics/available-campaigns');
      setAvailableCampaigns(response.data.data || []);
    } catch (error) {
      console.error('Error loading available campaigns:', error);
    }
  };

  const handlePresetChange = (days) => {
    setSelectedPreset(days);
    setDateRange([
      moment().subtract(days, 'days'),
      moment()
    ]);
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => moment(date).format('MMM DD, YYYY'),
      sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix(),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Campaign',
      dataIndex: 'campaignName',
      key: 'campaignName',
      width: 200,
      fixed: 'left'
    },
    {
      title: 'Status',
      dataIndex: 'campaignStatus',
      key: 'campaignStatus',
      width: 100,
      render: (status) => (
        <Tag color={status === 'Enabled' ? 'green' : 'default'}>
          {status}
        </Tag>
      )
    },
    {
      title: 'Phone Calls',
      dataIndex: 'phoneCalls',
      key: 'phoneCalls',
      width: 110,
      align: 'right',
      render: (calls) => <strong>{calls}</strong>,
      sorter: (a, b) => a.phoneCalls - b.phoneCalls
    },
    {
      title: 'Conversions',
      dataIndex: 'conversions',
      key: 'conversions',
      width: 120,
      align: 'right',
      render: (conversions) => (
        <Tag color="green">{conversions.toFixed(2)}</Tag>
      ),
      sorter: (a, b) => a.conversions - b.conversions
    },
    {
      title: 'Conv. Rate',
      dataIndex: 'conversionRate',
      key: 'conversionRate',
      width: 100,
      align: 'right',
      render: (rate) => `${rate.toFixed(1)}%`,
      sorter: (a, b) => a.conversionRate - b.conversionRate
    },
    {
      title: 'Impressions',
      dataIndex: 'impressions',
      key: 'impressions',
      width: 120,
      align: 'right',
      render: (impr) => impr.toLocaleString()
    },
    {
      title: 'Clicks',
      dataIndex: 'clicks',
      key: 'clicks',
      width: 90,
      align: 'right',
      render: (clicks) => clicks.toLocaleString()
    },
    {
      title: 'Cost',
      dataIndex: 'cost',
      key: 'cost',
      width: 100,
      align: 'right',
      render: (cost) => `$${cost.toFixed(2)}`,
      sorter: (a, b) => a.cost - b.cost
    },
    {
      title: 'CP/Call',
      dataIndex: 'cpCall',
      key: 'cpCall',
      width: 100,
      align: 'right',
      render: (cpCall) => `$${cpCall.toFixed(2)}`,
      sorter: (a, b) => a.cpCall - b.cpCall
    },
    {
      title: 'Cost/Conv.',
      dataIndex: 'costPerConversion',
      key: 'costPerConversion',
      width: 110,
      align: 'right',
      render: (cost) => cost > 0 ? `$${cost.toFixed(2)}` : '-'
    }
  ];

  // Calculate totals
  const totals = campaigns.reduce((acc, campaign) => ({
    phoneCalls: acc.phoneCalls + campaign.phoneCalls,
    conversions: acc.conversions + campaign.conversions,
    cost: acc.cost + campaign.cost,
    clicks: acc.clicks + campaign.clicks,
    impressions: acc.impressions + campaign.impressions
  }), { phoneCalls: 0, conversions: 0, cost: 0, clicks: 0, impressions: 0 });

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={4}>
            <Statistic 
              title="Total Phone Calls" 
              value={totals.phoneCalls}
              prefix={<FontAwesomeIcon icon={faGoogle} />}
            />
          </Col>
          <Col span={4}>
            <Statistic 
              title="Total Conversions" 
              value={totals.conversions.toFixed(2)}
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col span={4}>
            <Statistic 
              title="Total Cost" 
              value={totals.cost.toFixed(2)}
              prefix={<FontAwesomeIcon icon={faDollarSign} />}
              precision={2}
            />
          </Col>
          <Col span={4}>
            <Statistic 
              title="Avg CP/Call" 
              value={totals.phoneCalls > 0 ? (totals.cost / totals.phoneCalls).toFixed(2) : 0}
              prefix="$"
            />
          </Col>
          <Col span={4}>
            <Statistic 
              title="Total Clicks" 
              value={totals.clicks}
            />
          </Col>
          <Col span={4}>
            <Statistic 
              title="Total Impressions" 
              value={totals.impressions}
            />
          </Col>
        </Row>
      </Card>

      <Card 
        title={
          <Space>
            <FontAwesomeIcon icon={faGoogle} />
            <span>Google Ads Campaign Data</span>
            <Tag color="blue">{campaigns.length} records</Tag>
          </Space>
        }
        extra={
          <Space>
            <Select
              value={selectedCampaign}
              onChange={(value) => {
                setSelectedCampaign(value);
              }}
              style={{ width: 200 }}
            >
              <Option value="all">All Campaigns</Option>
              {availableCampaigns.map(c => (
                <Option key={c.campaignName} value={c.campaignName}>
                  {c.campaignName}
                </Option>
              ))}
            </Select>
            <Select
              value={selectedPreset}
              onChange={handlePresetChange}
              style={{ width: 150 }}
              placeholder="Select period"
            >
              {datePresets.map(preset => (
                <Option key={preset.value} value={preset.value}>
                  {preset.label}
                </Option>
              ))}
            </Select>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates.length === 2) {
                  setDateRange(dates);
                  setSelectedPreset(null); // Clear preset when manually selecting dates
                }
              }}
              format="MMM DD, YYYY"
              allowClear={false}
              disabledDate={(current) => {
                // Disable future dates
                return current && current > moment().endOf('day');
              }}
              presets={[
                { label: 'Last 7 Days', value: [moment().subtract(7, 'days'), moment()] },
                { label: 'Last 30 Days', value: [moment().subtract(30, 'days'), moment()] },
                { label: 'Last 3 Months', value: [moment().subtract(90, 'days'), moment()] }
              ]}
            />
            <Button 
              icon={<FontAwesomeIcon icon={faSync} />} 
              onClick={loadCampaigns}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={campaigns}
          loading={loading}
          rowKey="_id"
          pagination={{
            defaultPageSize: 50,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} records`,
            pageSizeOptions: ['20', '50', '100', '200']
          }}
          size="small"
          scroll={{ x: 1400 }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 'bold' }}>
                <Table.Summary.Cell index={0} colSpan={3}>TOTAL</Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">{totals.phoneCalls}</Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">{totals.conversions.toFixed(2)}</Table.Summary.Cell>
                <Table.Summary.Cell index={5} />
                <Table.Summary.Cell index={6} align="right">{totals.impressions.toLocaleString()}</Table.Summary.Cell>
                <Table.Summary.Cell index={7} align="right">{totals.clicks.toLocaleString()}</Table.Summary.Cell>
                <Table.Summary.Cell index={8} align="right">${totals.cost.toFixed(2)}</Table.Summary.Cell>
                <Table.Summary.Cell index={9} />
                <Table.Summary.Cell index={10} />
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>
    </div>
  );
};

export default GoogleAdsCampaignsTable;
